import { SELECT_ALL_VALUE } from "@/core/components/shared/data-table";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_STATUSES,
  type NotificationChannel,
  type NotificationStatus,
} from "@/features/shared/api/enums";

export interface NotificationLogsFiltersValue {
  status?: NotificationStatus;
  channel?: NotificationChannel;
  partyId?: string;
}

/**
 * Barre de filtres de l'historique d'envoi (`POST /NotificationLogs/all` —
 * champs du `NotificationLogFilter`). La sentinelle `SELECT_ALL_VALUE`
 * représente « pas de filtre » ; les valeurs sont des énumérations
 * MAJUSCULES transmises littéralement à l'API (annexe A).
 */
export function NotificationLogsFilters({
  value,
  onChange,
}: {
  value: NotificationLogsFiltersValue;
  onChange: (value: NotificationLogsFiltersValue) => void;
}) {
  const { t } = useNotificationsTranslation();

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1.5">
        <Label>{t("notifications.logs.filters.status")}</Label>
        <Select
          value={value.status ?? SELECT_ALL_VALUE}
          onValueChange={(v) =>
            onChange({
              ...value,
              status:
                v === SELECT_ALL_VALUE ? undefined : (v as NotificationStatus),
            })
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_ALL_VALUE}>
              {t("notifications.logs.filters.all")}
            </SelectItem>
            {NOTIFICATION_STATUSES.map((status) => (
              <SelectItem key={status} value={status} className="notranslate">
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>{t("notifications.logs.filters.channel")}</Label>
        <Select
          value={value.channel ?? SELECT_ALL_VALUE}
          onValueChange={(v) =>
            onChange({
              ...value,
              channel:
                v === SELECT_ALL_VALUE ? undefined : (v as NotificationChannel),
            })
          }
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SELECT_ALL_VALUE}>
              {t("notifications.logs.filters.all")}
            </SelectItem>
            {NOTIFICATION_CHANNELS.map((channel) => (
              <SelectItem key={channel} value={channel} className="notranslate">
                {channel}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="logs-party-id">
          {t("notifications.logs.filters.partyId")}
        </Label>
        <Input
          id="logs-party-id"
          value={value.partyId ?? ""}
          placeholder={t("notifications.logs.filters.partyIdPlaceholder")}
          className="w-72 font-mono"
          onChange={(e) =>
            onChange({
              ...value,
              partyId: e.target.value ? e.target.value : undefined,
            })
          }
        />
      </div>
    </div>
  );
}
